import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  TrendingUp,
  LocalOffer,
  AttachMoney,
  Group,
  CreditCard,
  Assignment,
  History
} from '@mui/icons-material';
import api from '../../../services/api/client';

// Sub-components
import PlansManagement from './PlansManagement';
import PacksManagement from './PacksManagement';
import CostsManagement from './CostsManagement';
import PromotionsManagement from './PromotionsManagement';
import AuditLogPanel from './AuditLogPanel';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`credits-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color = 'primary', subtitle }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: `${color}.light`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Icon sx={{ fontSize: 32, color: `${color}.main` }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function CreditsAdminPanel() {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);

  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/credits/dashboard');
      setDashboardStats(response.data);
    } catch (err) {
      console.error('Error fetching dashboard stats:', err);
      setError(err.response?.data?.detail || 'Erreur de chargement des statistiques');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box mb={3}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Administration des Credits
        </Typography>
        <Typography color="text.secondary">
          Gestion des plans, packs, promotions et coûts des actions
        </Typography>
      </Box>

      {/* Dashboard Stats */}
      {dashboardStats && (
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Organisations"
              value={dashboardStats.total_organizations}
              icon={Group}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Abonnements Actifs"
              value={dashboardStats.active_subscriptions}
              icon={CreditCard}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Credits Distribues (Aujourd'hui)"
              value={dashboardStats.total_credits_distributed_today}
              icon={TrendingUp}
              color="info"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Revenus du Mois"
              value={`${dashboardStats.total_revenue_this_month.toFixed(2)} EUR`}
              icon={AttachMoney}
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Plans Distribution */}
      {dashboardStats && Object.keys(dashboardStats.plans_distribution).length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            Distribution des Plans
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            {Object.entries(dashboardStats.plans_distribution).map(([plan, count]) => (
              <Chip
                key={plan}
                label={`${plan}: ${count}`}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Paper>
      )}

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<CreditCard />} iconPosition="start" label="Plans" />
          <Tab icon={<Assignment />} iconPosition="start" label="Packs" />
          <Tab icon={<AttachMoney />} iconPosition="start" label="Coûts Actions" />
          <Tab icon={<LocalOffer />} iconPosition="start" label="Promotions" />
          <Tab icon={<History />} iconPosition="start" label="Audit Log" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          <TabPanel value={activeTab} index={0}>
            <PlansManagement onUpdate={fetchDashboardStats} />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <PacksManagement onUpdate={fetchDashboardStats} />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            <CostsManagement onUpdate={fetchDashboardStats} />
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <PromotionsManagement onUpdate={fetchDashboardStats} />
          </TabPanel>
          <TabPanel value={activeTab} index={4}>
            <AuditLogPanel />
          </TabPanel>
        </Box>
      </Paper>
    </Box>
  );
}
